import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
} from "react-table";
import React, { useState, useMemo } from "react";

import TableLoader from "./TableLoader/TableLoader";
import ZIcon from "./ZIcon/ZIcon";
import NoResults from "./NoResults/NoResults";
import { parseTableColumnsForCell } from "./parsers/cellParser";
import TablePagination from "./TablePagination";
import styled from "styled-components";

function AdvancedTable({
  columns = [],
  data = [],
  setData,
  isLoading,
  editPrimaryColumns = [],
  hiddenColumns = [],
  actions,
  header = true,
  TableBelowHeader,
  pagination = true,
  tableTitle,
  onEdit,
}) {
  const parsedColumnConfig = useMemo(
    () => parseTableColumnsForCell(columns),
    [columns]
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
    globalFilter,
    state: { pageIndex, pageSize },
    page,
    pageCount,
    gotoPage,
  } = useTable(
    {
      columns: parsedColumnConfig,
      data,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const handlePageChange = (page) => {
    gotoPage(page - 1);
  };

  return (
    <div>
      {header && (
        <TableHeaderDiv>
          <SearchContainer>
            <input
              type="text"
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search"
            />
            <SearchIcon unicode="E806" />
          </SearchContainer>
          <RightUtilsDiv>{actions}</RightUtilsDiv>
        </TableHeaderDiv>
      )}
      {tableTitle && TableBelowHeader && (
        <TableHeading>{tableTitle}</TableHeading>
      )}
      {
        <TableWrapper>
          <TableInnerWrapper>
            <Table {...getTableProps()}>
              <TableHead>
                {headerGroups?.map((headerGroup, headerGroupIdx) => {
                  return (
                    <TableHeadRow
                      key={headerGroup.id}
                      {...headerGroup.getHeaderGroupProps()}
                    >
                      {headerGroup.headers.map((column, headerIdx) => {
                        const canSort = column?.isSortable;

                        if (hiddenColumns.includes(column)) return null;

                        return (
                          <TableHeadData
                            {...column.getHeaderProps(
                              canSort ? column.getSortByToggleProps() : {}
                            )}
                            key={headerIdx + column.id}
                          >
                            <CellWidthLimiter>
                              <div className="first-letter:uppercase">
                                {column.render("Header")}
                              </div>

                              <HeaderUtilDiv className="ml-auto flex items-center gap-2 pl-2">
                                {canSort && (
                                  <div className="text-gray-400">
                                    {column.isSorted ? (
                                      column.isSortedDesc ? (
                                        <ZIcon unicode="E85A" />
                                      ) : (
                                        <ZIcon unicode="E85B" />
                                      )
                                    ) : canSort ? (
                                      <ZIcon unicode="EC6D" />
                                    ) : null}
                                  </div>
                                )}
                              </HeaderUtilDiv>
                            </CellWidthLimiter>
                          </TableHeadData>
                        );
                      })}
                    </TableHeadRow>
                  );
                })}
              </TableHead>
              {page?.length > 0 && !isLoading && (
                <TableBody {...getTableBodyProps()}>
                  {page?.map((row, rowIdx) => {
                    prepareRow(row);
                    return (
                      <TableBodyRow
                        key={rowIdx + row.id}
                        {...row.getRowProps()}
                      >
                        {row?.cells.map((cell) => {
                          if (hiddenColumns.includes(cell.column.Header))
                            return null;

                          return (
                            <TableBodyData
                              key={cell.id}
                              {...cell.getCellProps()}
                            >
                              {cell.render("Cell")}
                            </TableBodyData>
                          );
                        })}
                      </TableBodyRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
            <TableLoader isLoading={isLoading} />
            {rows?.length === 0 && !isLoading && <NoResults />}
          </TableInnerWrapper>
          {page?.length && pagination ? (
            <TablePagination
              currentPageRowCount={page?.length || 0}
              totalCount={data?.length || 0}
              pageCount={pageCount}
              currentPage={pageIndex + 1}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              // message={'Showing filtered results'}
            />
          ) : null}
        </TableWrapper>
      }
      {TableBelowHeader && (
        <TableBelowHeaderContainer>
          {TableBelowHeader}
        </TableBelowHeaderContainer>
      )}
    </div>
  );
}

export default AdvancedTable;

const TableBelowHeaderContainer = styled.div`
  margin-top: 30px;
`;

const TableHeading = styled.div`
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 10px;
`;

const TableHeaderDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 0.3rem;
  margin-bottom: 16px;
  gap: 0.5rem;
`;

const RightUtilsDiv = styled.div`
  margin-left: auto;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border: 1px solid #e3ebed;
  border-radius: 0.375rem;
  background-color: #fff;
  width: 300px;

  input {
    border: none;
    outline: none;
    width: 100%;
  }
`;

const SearchIcon = styled(ZIcon)`
  color: #d2d3d6;
  font-size: 14px;
  cursor: pointer;
`;

const TableWrapper = styled.div`
  border: 1px solid #e3ebed;
  border-radius: 0.375rem;
  max-height: 100%;
  overflow: hidden;
`;

const TableInnerWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  background: #fff;
`;

const Table = styled.table`
  width: 100%;
  overflow-x: auto;
  border-radius: 0.375rem;
  text-align: left;
  font-size: 14px;
  color: #374151;
`;

const TableHead = styled.thead`
  background-color: #f1f7f9;
  font-size: 13px;
  text-transform: uppercase;
`;

const TableHeadRow = styled.tr`
  padding: 0.75rem;
  font-weight: 500;
`;

const TableHeadData = styled.th`
  padding: 0.75rem;
`;

const CellWidthLimiter = styled.div`
  display: flex;
  max-width: 440px;
  width: max-content;
  align-items: center;
  gap: 0.5rem;
`;

const TableBody = styled.tbody`
  font-size: 14px;
`;

const TableBodyRow = styled.tr`
  border-bottom: 1px solid #e3ebed;
  background-color: #fff;
  font-size: 13px;
`;

const TableBodyData = styled.td`
  padding: 0.75rem;
`;

const HeaderUtilDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-left: 0.5rem;
  margin-left: auto;
`;
